<template>
  <div class="admin-dashboard">
    <div class="dashboard-toolbar">
      <div>
        <h1>Platform overview</h1>
        <p>Account growth, subscriptions, and customer activity at a glance.</p>
      </div>
      <button class="refresh-button" type="button" :disabled="loading" @click="fetchDashboard">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ spinning: loading }">
          <polyline points="23 4 23 10 17 10"/>
          <polyline points="1 20 1 14 7 14"/>
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
        </svg>
        {{ loading ? 'Refreshing' : 'Refresh' }}
      </button>
    </div>

    <div v-if="errorMessage" class="dashboard-alert" role="alert">
      <span>{{ errorMessage }}</span>
      <button type="button" @click="fetchDashboard">Try again</button>
    </div>

    <template v-if="loading && !hasLoaded">
      <div class="stats-grid loading-grid">
        <div v-for="index in 6" :key="index" class="stat-card skeleton-card">
          <span class="skeleton skeleton-label"></span>
          <span class="skeleton skeleton-value"></span>
        </div>
      </div>
      <div class="dashboard-grid">
        <div class="panel chart-panel skeleton-panel"></div>
        <div class="panel skeleton-panel"></div>
      </div>
    </template>

    <template v-else>
      <div class="stats-grid">
        <article v-for="card in statCards" :key="card.key" class="stat-card" :class="card.tone">
          <div class="stat-card-top">
            <span class="stat-label">{{ card.label }}</span>
            <span class="stat-icon" v-html="card.icon"></span>
          </div>
          <strong>{{ card.value }}</strong>
          <span class="stat-context">{{ card.context }}</span>
        </article>
      </div>

      <div class="dashboard-grid primary-grid">
        <section class="panel chart-panel">
          <div class="panel-header">
            <div>
              <h2>Account growth</h2>
              <p>New businesses over the last six months</p>
            </div>
            <span class="panel-badge">6 months</span>
          </div>
          <div v-if="chartData.labels.length" class="chart-wrap">
            <chart-js-widget type="bar" :data="chartData" :options="chartOptions" />
          </div>
          <div v-else class="empty-state compact">No signup data is available yet.</div>
        </section>

        <section class="panel signup-panel">
          <div class="panel-header">
            <div>
              <h2>Recent signups</h2>
              <p>Newest businesses on the platform</p>
            </div>
            <router-link to="/admin/accounts" class="text-link">View all</router-link>
          </div>
          <div v-if="recentSignups.length" class="account-list">
            <button
              v-for="account in recentSignups.slice(0, 6)"
              :key="account.id"
              class="account-row"
              type="button"
              @click="openAccount(account.id)"
            >
              <span class="account-avatar">{{ initials(account.name) }}</span>
              <span class="account-copy">
                <strong>{{ account.name || 'Unnamed business' }}</strong>
                <small>{{ account.owner_email || account.email || 'No email' }}</small>
              </span>
              <span class="account-meta">
                <span class="status-pill" :class="account.status || 'inactive'">{{ account.status || 'inactive' }}</span>
                <small>{{ relativeDate(account.created_at) }}</small>
              </span>
            </button>
          </div>
          <div v-else class="empty-state compact">No businesses have signed up yet.</div>
        </section>
      </div>

      <div class="dashboard-grid activity-grid">
        <section class="panel">
          <div class="panel-header">
            <div>
              <h2>Recently active</h2>
              <p>Owners with the latest activity</p>
            </div>
            <span class="activity-dot active-dot"></span>
          </div>
          <activity-table :accounts="recentActives" mode="active" @open="openAccount" />
        </section>

        <section class="panel">
          <div class="panel-header">
            <div>
              <h2>Needs attention</h2>
              <p>Owners inactive for the longest time</p>
            </div>
            <span class="activity-dot warning-dot"></span>
          </div>
          <activity-table :accounts="mostInactives" mode="inactive" @open="openAccount" />
        </section>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import ChartJsWidget from '../../components/ChartJsWidget.vue'
import ActivityTable from '../../components/admin/AdminActivityTable.vue'

export default {
  name: 'AdminDashboard',
  components: { ChartJsWidget, ActivityTable },
  data() {
    return {
      loading: false,
      hasLoaded: false,
      errorMessage: '',
      stats: {
        totalBusinesses: 0,
        totalUsers: 0,
        activeSubscriptions: 0,
        weeklySignups: 0,
        monthlySignups: 0,
        totalRevenue: 0,
        billingInvoiceCount: 0
      },
      recentSignups: [],
      recentActives: [],
      mostInactives: [],
      monthlyTrend: []
    }
  },
  computed: {
    statCards() {
      return [
        { key: 'businesses', label: 'Total businesses', value: this.number(this.stats.totalBusinesses), context: `${this.number(this.stats.monthlySignups)} joined this month`, tone: 'indigo', icon: '↗' },
        { key: 'users', label: 'Platform users', value: this.number(this.stats.totalUsers), context: 'Across all businesses', tone: 'blue', icon: '◎' },
        { key: 'subscriptions', label: 'Active subscriptions', value: this.number(this.stats.activeSubscriptions), context: this.subscriptionRate, tone: 'green', icon: '✓' },
        { key: 'weekly', label: 'New this week', value: this.number(this.stats.weeklySignups), context: 'Businesses created', tone: 'violet', icon: '+' },
        { key: 'monthly', label: 'New this month', value: this.number(this.stats.monthlySignups), context: 'Last 30 days', tone: 'orange', icon: '↗' },
        {
          key: 'revenue',
          label: 'Total billed',
          value: this.currency(this.stats.totalRevenue),
          context: `${this.number(this.stats.billingInvoiceCount)} billing invoices`,
          tone: 'teal',
          icon: 'DH'
        }
      ]
    },
    subscriptionRate() {
      const total = Number(this.stats.totalBusinesses) || 0
      const active = Number(this.stats.activeSubscriptions) || 0
      return total ? `${Math.round((active / total) * 100)}% of all businesses` : 'No businesses yet'
    },
    chartData() {
      return {
        labels: this.monthlyTrend.map(item => moment(`${item.month}-01`).format('MMM YYYY')),
        datasets: [{
          label: 'New businesses',
          data: this.monthlyTrend.map(item => Number(item.count) || 0),
          backgroundColor: '#6366f1',
          hoverBackgroundColor: '#4f46e5',
          borderWidth: 0,
          barPercentage: 0.62,
          categoryPercentage: 0.72
        }]
      }
    },
    chartOptions() {
      return {
        legend: { display: false },
        tooltips: {
          backgroundColor: '#111827',
          titleFontColor: '#fff',
          bodyFontColor: '#e5e7eb',
          displayColors: false,
          cornerRadius: 8,
          xPadding: 12,
          yPadding: 10
        },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#64748b', fontSize: 11 } }],
          yAxes: [{
            gridLines: { color: '#eef2f7', drawBorder: false },
            ticks: { beginAtZero: true, precision: 0, fontColor: '#94a3b8', padding: 8 }
          }]
        }
      }
    }
  },
  created() {
    this.fetchDashboard()
  },
  methods: {
    async fetchDashboard() {
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await axios.get(`${process.env.VUE_APP_API}/admin/dashboard/stats`)
        if (response.data.success) {
          this.stats = { ...this.stats, ...response.data.stats }
          this.recentSignups = response.data.recentSignups || []
          this.recentActives = response.data.recentActives || []
          this.mostInactives = response.data.mostInactives || []
          this.monthlyTrend = response.data.monthlyTrend || []
          this.hasLoaded = true
        }
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          this.$router.push('/admin/login')
          return
        }
        this.errorMessage = error.response?.data?.message || 'Dashboard data could not be loaded.'
      } finally {
        this.loading = false
      }
    },
    openAccount(id) {
      if (id) this.$router.push(`/admin/accounts/${id}`)
    },
    number(value) {
      return new Intl.NumberFormat('en-US').format(Number(value) || 0)
    },
    currency(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'MAD', maximumFractionDigits: 0
      }).format(Number(value) || 0)
    },
    initials(name) {
      return (name || 'Business').split(/\s+/).slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase()
    },
    relativeDate(value) {
      return value ? moment(value).fromNow() : 'Unknown'
    }
  }
}
</script>

<style scoped>
.admin-dashboard { display: flex; flex-direction: column; gap: 24px; color: #1f2937; }
.dashboard-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.dashboard-toolbar h1 { margin: 0 0 5px; font-size: 24px; line-height: 1.2; color: #111827; letter-spacing: -0.5px; }
.dashboard-toolbar p, .panel-header p { margin: 0; color: #64748b; font-size: 13px; }
.refresh-button { display: inline-flex; align-items: center; gap: 8px; padding: 9px 13px; border: 1px solid #dbe2ea; border-radius: 9px; background: #fff; color: #475569; font-size: 13px; font-weight: 600; cursor: pointer; }
.refresh-button:hover:not(:disabled) { border-color: #a5b4fc; color: #4338ca; }
.refresh-button:disabled { cursor: wait; opacity: .7; }
.spinning { animation: spin .8s linear infinite; }
.dashboard-alert { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 12px 15px; color: #b42318; background: #fff1f0; border: 1px solid #fecaca; border-radius: 10px; font-size: 13px; }
.dashboard-alert button { color: #b42318; background: transparent; border: 0; font-weight: 700; cursor: pointer; }
.stats-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 14px; }
.stat-card { min-width: 0; padding: 17px; background: #fff; border: 1px solid #e5e7eb; border-radius: 13px; box-shadow: 0 1px 2px rgba(15, 23, 42, .03); }
.stat-card-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.stat-label { color: #64748b; font-size: 12px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.stat-icon { display: grid; place-items: center; width: 25px; height: 25px; border-radius: 7px; font-size: 13px; font-weight: 800; }
.stat-card strong { display: block; margin-top: 12px; color: #111827; font-size: 24px; line-height: 1; letter-spacing: -0.6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.stat-context { display: block; margin-top: 8px; color: #94a3b8; font-size: 11px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.indigo .stat-icon { color: #4f46e5; background: #eef2ff; }.blue .stat-icon { color: #2563eb; background: #eff6ff; }.green .stat-icon { color: #059669; background: #ecfdf5; }.violet .stat-icon { color: #7c3aed; background: #f5f3ff; }.orange .stat-icon { color: #d97706; background: #fffbeb; }.teal .stat-icon { color: #0f766e; background: #f0fdfa; }
.dashboard-grid { display: grid; gap: 18px; }.primary-grid { grid-template-columns: minmax(0, 1.25fr) minmax(360px, .75fr); }.activity-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.panel { min-width: 0; padding: 20px; background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; box-shadow: 0 1px 3px rgba(15, 23, 42, .035); }
.panel-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 18px; }
.panel-header h2 { margin: 0 0 4px; color: #111827; font-size: 15px; font-weight: 700; }
.panel-badge { padding: 5px 8px; color: #6366f1; background: #eef2ff; border-radius: 20px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; }
.text-link { color: #4f46e5; font-size: 12px; font-weight: 700; text-decoration: none; }.text-link:hover { text-decoration: underline; }
.chart-wrap { height: 280px; }
.account-list { display: flex; flex-direction: column; }
.account-row { width: 100%; display: flex; align-items: center; gap: 11px; padding: 11px 4px; text-align: left; background: transparent; border: 0; border-bottom: 1px solid #f1f5f9; cursor: pointer; }
.account-row:last-child { border-bottom: 0; }.account-row:hover .account-copy strong { color: #4f46e5; }
.account-avatar { flex: 0 0 auto; display: grid; place-items: center; width: 34px; height: 34px; color: #4f46e5; background: #eef2ff; border-radius: 9px; font-size: 11px; font-weight: 800; }
.account-copy { min-width: 0; flex: 1; }.account-copy strong, .account-copy small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }.account-copy strong { color: #1f2937; font-size: 12px; }.account-copy small { margin-top: 3px; color: #94a3b8; font-size: 10px; }
.account-meta { flex: 0 0 auto; display: flex; align-items: flex-end; flex-direction: column; gap: 4px; }.account-meta small { color: #94a3b8; font-size: 10px; }
.status-pill { padding: 3px 6px; border-radius: 10px; color: #64748b; background: #f1f5f9; font-size: 9px; font-weight: 700; text-transform: capitalize; }.status-pill.active { color: #047857; background: #ecfdf5; }.status-pill.trial { color: #7c3aed; background: #f5f3ff; }.status-pill.canceled { color: #b91c1c; background: #fef2f2; }
.activity-dot { width: 9px; height: 9px; margin-top: 5px; border-radius: 50%; }.active-dot { background: #10b981; box-shadow: 0 0 0 4px #ecfdf5; }.warning-dot { background: #f59e0b; box-shadow: 0 0 0 4px #fffbeb; }
.panel ::v-deep .activity-table-wrap { overflow-x: auto; }.panel ::v-deep .activity-table { width: 100%; border-collapse: collapse; font-size: 11px; }.panel ::v-deep .activity-table th { padding: 0 10px 9px; color: #94a3b8; text-align: left; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; }.panel ::v-deep .activity-table td { padding: 11px 10px; color: #64748b; border-top: 1px solid #f1f5f9; white-space: nowrap; }.panel ::v-deep .activity-table td:first-child { max-width: 150px; }.panel ::v-deep .activity-table strong { display: block; color: #334155; overflow: hidden; text-overflow: ellipsis; }.panel ::v-deep .activity-time.active { color: #047857; }.panel ::v-deep .activity-time.inactive { color: #b45309; }.panel ::v-deep .row-action { width: 27px; height: 27px; color: #64748b; background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 7px; cursor: pointer; }.panel ::v-deep .row-action:hover { color: #4f46e5; border-color: #c7d2fe; background: #eef2ff; }
.empty-state { display: grid; place-items: center; min-height: 160px; color: #94a3b8; font-size: 12px; }.empty-state.compact { min-height: 130px; }
.skeleton-panel { min-height: 330px; }.skeleton { display: block; border-radius: 6px; background: linear-gradient(90deg, #f1f5f9 25%, #e8edf4 37%, #f1f5f9 63%); background-size: 400% 100%; animation: shimmer 1.4s ease infinite; }.skeleton-label { width: 65%; height: 12px; }.skeleton-value { width: 45%; height: 25px; margin-top: 18px; }
@keyframes spin { to { transform: rotate(360deg); } } @keyframes shimmer { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }
@media (max-width: 1250px) { .stats-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 900px) { .primary-grid, .activity-grid { grid-template-columns: 1fr; }.chart-wrap { height: 250px; } }
@media (max-width: 620px) { .admin-dashboard { gap: 16px; }.dashboard-toolbar { align-items: flex-start; }.dashboard-toolbar h1 { font-size: 20px; }.dashboard-toolbar p { display: none; }.refresh-button { padding: 8px; }.refresh-button svg { margin: 0; }.stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }.stat-card { padding: 14px; }.stat-card strong { font-size: 20px; }.panel { padding: 15px; }.primary-grid, .activity-grid { gap: 12px; }.panel ::v-deep .activity-table th:nth-child(2), .panel ::v-deep .activity-table td:nth-child(2) { display: none; } }
</style>
